import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { Search, FilterX, Plus, UserCheck, RefreshCw } from "lucide-react";
import { ageingBucketColor } from "@/lib/statusStyles";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";

export default function ComplaintList() {
  const { user } = useAuth();
  const readOnly = user?.role === "sales";
  const [complaints, setComplaints] = useState([]);
  const [enums, setEnums] = useState({ complaint_statuses: [] });
  const [brands, setBrands] = useState([]);
  const [techs, setTechs] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [filters, setFilters] = useState({
    search: "", status: "", brand: "", technician_id: "", province: "",
    warranty_status: "", service_type: "", date_from: "", date_to: "", ageing_bucket: "",
    product_category: "", product_size: "",
  });
  const [warrantyTypes, setWarrantyTypes] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [productMaster, setProductMaster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [statuses, setStatuses] = useState([]);
  const [enumsList, setEnumsList] = useState([]);

  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelected((s) => s.size === complaints.length ? new Set() : new Set(complaints.map((c) => c.id)));

  const fetchList = async () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v));
    const { data } = await api.get("/complaints", { params });
    setComplaints(data);
    setLoading(false);
  };

  useEffect(() => {
    api.get("/meta/enums").then((r) => { setEnums(r.data); setStatuses(r.data.complaint_statuses); });
    api.get("/masters/brands").then((r) => setBrands(r.data));
    api.get("/technicians").then((r) => setTechs(r.data));
    api.get("/masters/provinces").then((r) => setProvinces(r.data));
    api.get("/masters/warranty-types").then((r) => setWarrantyTypes(r.data));
    api.get("/masters/service-types").then((r) => setServiceTypes(r.data));
    api.get("/products").then((r) => setProductMaster(r.data));
  }, []);

  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [filters]);

  const resetFilters = () => setFilters({
    search: "", status: "", brand: "", technician_id: "", province: "",
    warranty_status: "", service_type: "", date_from: "", date_to: "", ageing_bucket: "",
    product_category: "", product_size: "",
  });

  const categoryOptions = Array.from(new Set(productMaster.map((p) => p.product_category).filter(Boolean))).sort();
  const sizeOptions = Array.from(new Set(productMaster.map((p) => p.product_size).filter(Boolean))).sort();

  return (
    <div className="space-y-5" data-testid="complaints-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Complaints</h1>
          <p className="text-sm text-slate-500 mt-1">{complaints.length} records · {selected.size} selected {loading && "· loading..."}</p>
        </div>
        <div className="flex gap-2">
          {!readOnly && selected.size > 0 && <BulkBar selected={selected} techs={techs} statuses={statuses} onDone={() => { setSelected(new Set()); fetchList(); }} />}
          {!readOnly && (
            <Link to="/complaints/new">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-complaint-btn">
                <Plus size={16} className="mr-1" /> New Complaint
              </Button>
            </Link>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div className="col-span-2 lg:col-span-2 relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                placeholder="Search complaint #, customer, phone, serial..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-8"
                data-testid="search-input"
              />
            </div>
            <FilterSelect label="Status" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} options={enums.complaint_statuses} testId="filter-status" />
            <FilterSelect label="Brand" value={filters.brand} onChange={(v) => setFilters({ ...filters, brand: v })} options={brands.map((b) => b.name)} testId="filter-brand" />
            <FilterSelect label="Technician" value={filters.technician_id} onChange={(v) => setFilters({ ...filters, technician_id: v })}
              options={techs.map((t) => ({ value: t.id, label: t.name }))} testId="filter-technician" />
            <FilterSelect label="Province" value={filters.province} onChange={(v) => setFilters({ ...filters, province: v })} options={provinces.map((p) => p.name)} testId="filter-province" />
            <FilterSelect label="Warranty" value={filters.warranty_status} onChange={(v) => setFilters({ ...filters, warranty_status: v })} options={warrantyTypes.map((w) => w.name)} testId="filter-warranty" />
            <FilterSelect label="Service Type" value={filters.service_type} onChange={(v) => setFilters({ ...filters, service_type: v })} options={serviceTypes.map((s) => s.name)} testId="filter-service-type" />
            <FilterSelect label="Category" value={filters.product_category} onChange={(v) => setFilters({ ...filters, product_category: v })} options={categoryOptions} testId="filter-product-category" />
            <FilterSelect label="Size" value={filters.product_size} onChange={(v) => setFilters({ ...filters, product_size: v })} options={sizeOptions} testId="filter-product-size" />
            <FilterSelect label="Ageing" value={filters.ageing_bucket} onChange={(v) => setFilters({ ...filters, ageing_bucket: v })} options={["0-1","2-3","4-7","8-15","16+"]} testId="filter-ageing" />
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">From</label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="filter-date-from" />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">To</label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="filter-date-to" />
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={resetFilters} className="w-full" data-testid="reset-filters-btn">
                <FilterX size={14} className="mr-1" /> Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full dense-table">
              <thead className="bg-slate-50 border-b sticky top-0 z-10">
                <tr>
                  <th className="text-left text-xs"><Checkbox checked={complaints.length>0 && selected.size === complaints.length} onCheckedChange={toggleAll} data-testid="select-all-checkbox" /></th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Complaint #</th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Registered</th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Customer</th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Province</th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Brand / Product</th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Category / Size</th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Warranty</th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Technician</th>
                  <th className="text-left text-xs uppercase tracking-wide text-slate-600 font-semibold">Status</th>
                  <th className="text-right text-xs uppercase tracking-wide text-slate-600 font-semibold">Ageing</th>
                </tr>
              </thead>
              <tbody>
                {complaints.length === 0 && (
                  <tr><td colSpan={11} className="text-center py-12 text-slate-400">No complaints match the filters.</td></tr>
                )}
                {complaints.map((c) => (
                  <tr key={c.id} data-testid={`complaint-row-${c.complaint_number}`} className={selected.has(c.id) ? "bg-blue-50" : ""}>
                    <td><Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleSelect(c.id)} data-testid={`select-${c.id}`} /></td>
                    <td><Link className="font-mono text-slate-900 font-medium hover:underline" to={`/complaints/${c.id}`}>{c.complaint_number}</Link></td>
                    <td className="text-slate-700">{c.registered_date_ad || "—"}</td>
                    <td>{c.customer_name}<div className="text-xs text-slate-500">{c.phone_number_1}</div></td>
                    <td className="text-slate-700">{c.province || "—"}</td>
                    <td>{c.brand}<div className="text-xs text-slate-500">{c.product_name} {c.product_model_number && <span className="font-mono">· {c.product_model_number}</span>}</div></td>
                    <td>{c.product_category || "—"}<div className="text-xs text-slate-500">{c.product_size || ""}</div></td>
                    <td className="text-slate-700">{c.warranty_status || "—"}</td>
                    <td className="text-slate-700">{c.technician_name || <span className="text-slate-400">—</span>}</td>
                    <td><StatusBadge status={c.status} /></td>
                    <td className={`text-right font-mono ${ageingBucketColor(c.ageing_days)}`}>{c.ageing_days != null ? `${c.ageing_days}d` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  // options can be string[] or {value,label}[]
  const norm = (options || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  return (
    <div>
      <label className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">{label}</label>
      <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
        <SelectTrigger className="h-9 text-sm" data-testid={testId}>
          <SelectValue placeholder={`All ${label.toLowerCase()}`} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All {label.toLowerCase()}</SelectItem>
          {norm.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function BulkBar({ selected, techs, statuses, onDone }) {
  const ids = Array.from(selected);
  const [assignOpen, setAssignOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [tid, setTid] = useState("");
  const [st, setSt] = useState("");
  const [remarks, setRemarks] = useState("");

  const submitAssign = async () => {
    try { await api.post("/complaints/bulk-assign", { complaint_ids: ids, technician_id: tid });
      toast.success(`${ids.length} complaints assigned`); setAssignOpen(false); setTid(""); onDone();
    } catch (e) { toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };
  const submitStatus = async () => {
    try { await api.post("/complaints/bulk-status", { complaint_ids: ids, status: st, remarks });
      toast.success(`${ids.length} complaints updated`); setStatusOpen(false); setSt(""); setRemarks(""); onDone();
    } catch (e) { toast.error("Failed", { description: formatApiErrorDetail(e.response?.data?.detail) }); }
  };

  return (
    <div className="flex gap-2" data-testid="bulk-bar">
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogTrigger asChild><Button variant="outline" data-testid="bulk-assign-btn"><UserCheck size={14} className="mr-1" /> Bulk Assign ({ids.length})</Button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>Bulk Assign Technician</DialogTitle></DialogHeader>
          <div>
            <Label>Technician</Label>
            <Select value={tid} onValueChange={setTid}>
              <SelectTrigger data-testid="bulk-tech-select"><SelectValue placeholder="Pick" /></SelectTrigger>
              <SelectContent>{techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} — {t.area}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={submitAssign} disabled={!tid} className="bg-slate-900 text-white" data-testid="confirm-bulk-assign">Assign {ids.length}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogTrigger asChild><Button variant="outline" data-testid="bulk-status-btn"><RefreshCw size={14} className="mr-1" /> Bulk Status ({ids.length})</Button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>Bulk Status Change</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>New Status</Label>
              <Select value={st} onValueChange={setSt}>
                <SelectTrigger data-testid="bulk-status-select"><SelectValue placeholder="Choose status" /></SelectTrigger>
                <SelectContent>{statuses.filter((s) => s !== "Closed").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Remarks</Label>
              <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Reason / note" />
            </div>
            <div className="text-xs text-slate-500">Closed status is excluded — close complaints individually with happy call.</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusOpen(false)}>Cancel</Button>
            <Button onClick={submitStatus} disabled={!st} className="bg-slate-900 text-white" data-testid="confirm-bulk-status">Update {ids.length}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}